import { createSelector, createSlice } from "@reduxjs/toolkit";
import { SORT, STOPS } from "src/constants";

function makeSearchFiltersSlice(
  sliceName,
  initialState = {
    sort: { type: SORT.best },
    filters: {
      price: {
        isOn: false,
        defaultValue: "",
        value: "",
        min: 0,
        max: 999,
      },
    },
  },
  reducers = {}
) {
  return createSlice({
    name: sliceName,
    initialState: {
      isSorting: false,
      isReceivingData: false,
      defaultSort: SORT.best,
      ...initialState,
    },
    reducers: {
      setDefaultSortType(state, action) {
        state.defaultSort = action.payload;
      },
      setSortType(state, action) {
        console.log("setting sort type " + action.payload);
        state.sort.type = action.payload;
      },
      resetSortType(state) {
        console.log("resetting sort type");
        state.sort.type = state.defaultSort;
      },
      setSortValue(state, action) {
        console.log("setting sort value " + action.payload.name);
        state.sort[action.payload.name] = action.payload.value;
      },
      resetSortValues(state) {
        console.log("resetting sort values");
        Object.keys(state.sort)
          .filter((key) => key !== "type")
          .forEach((key) => {
            const newVals = Object.fromEntries(
              Object.entries(state.sort[key]).map(([k, v]) => [k, -1])
            );
            state.sort[key] = { ...newVals };
          });
      },
      applyFilter(state, action) {
        console.log("applying filter " + action.payload.type);
        const filterType = action.payload.type;
        const filterValue = action.payload.value;

        // assumes default value is when filter is off
        if (filterValue !== state.filters[filterType].defaultValue) {
          state.filters[filterType].isOn = true;
        } else {
          state.filters[filterType].isOn = false;
        }
        state.filters[filterType].value = filterValue;
      },
      clearFilter(state, action) {
        console.log("clearing filter " + action.payload.type);
        const filterType = action.payload.type;
        state.filters[filterType].value = state.filters[filterType].defaultValue;
        state.filters[filterType].isOn = false;
      },
      clearAllFilters(state) {
        console.log("clearing all filters");
        Object.keys(state.filters).forEach((key) => {
          state.filters[key].value = state.filters[key].defaultValue;
          state.filters[key].isOn = false;
        });
      },
      setMinFilterValue(state, action) {
        console.log("setting min value " + action.payload.type);
        const filterType = action.payload.type;
        state.filters[filterType].min = action.payload.value;
      },
      setMaxFilterValue(state, action) {
        console.log("setting max value " + action.payload.type);
        const filterType = action.payload.type;
        state.filters[filterType].max = action.payload.value;
      },
      setIsSorting(state, action) {
        state.isSorting = action.payload;
      },
      setIsReceivingData(state, action) {
        state.isReceivingData = action.payload;
      },
      ...reducers,
    },
  });
}

export const flightsSlice = makeSearchFiltersSlice(
  "flights",
  {
    pinned: {
      departure: [],
      arrival: [],
    },
    sort: {
      type: SORT.best,
      best: { price: 0, duration: 0 },
      cheapest: { price: 0, duration: 0 },
      fastest: { price: 0, duration: 0 },
      departure: { price: 0, duration: 0 },
      arrival: { price: 0, duration: 0 },
    },
    filters: {
      cabinBaggage: {
        isOn: false,
        defaultValue: 0,
        value: 0,
        max: 1,
      },
      checkedBaggage: {
        isOn: false,
        defaultValue: 0,
        value: 0,
        max: 2,
      },
      stops: {
        isOn: false,
        defaultValue: STOPS.any,
        value: STOPS.any,
      },
      nightFlight: {
        isOn: false,
        defaultValue: "included",
        value: "included",
      },
      nightStop: {
        isOn: false,
        defaultValue: "included",
        value: "included",
      },
      duration: {
        isOn: false,
        defaultValue: "",
        value: "",
        max: 25,
      },
      stopover: {
        isOn: false,
        defaultValue: "",
        value: "",
        min: 0,
        max: 25,
      },
      departureStartTime: {
        isOn: false,
        defaultValue: "",
        value: "",
      },
      departureEndTime: {
        isOn: false,
        defaultValue: "",
        value: "",
      },
      returnStartTime: {
        isOn: false,
        defaultValue: "",
        value: "",
      },
      returnEndTime: {
        isOn: false,
        defaultValue: "",
        value: "",
      },
      airline: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        options: [],
      },
      airport: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        options: [],
      },
      price: {
        isOn: false,
        defaultValue: "",
        value: "",
        min: 0,
        max: 999,
      },
    },
  },
  {
    setAirportOptions(state, action) {
      console.log("setting airport options");
      state.filters.airport.options = action.payload.options;
    },
    setAirlineOptions(state, action) {
      console.log("setting airline options");
      state.filters.airline.options = action.payload.options;
    },
    updatePinnedDepartureSegment(state, action) {
      console.log("pinning departure flight");
      state.pinned.departure = action.payload;
    },
    updatePinnedArrivalSegment(state, action) {
      console.log("pinning arrival flight");
      state.pinned.arrival = action.payload;
    },
  }
);

export const hotelsSlice = makeSearchFiltersSlice(
  "hotels",
  {
    defaultSort: SORT.cheapest,
    sort: {
      type: SORT.cheapest,
      cheapest: { price: 0, rating: 0 },
      starRating: { price: 0, rating: 0 },
      reviewScore: { price: 0, rating: 0 },
      closest: { price: 0, rating: 0 },
    },
    filters: {
      pricePerNight: {
        isOn: false,
        defaultValue: "",
        value: "",
        min: 0,
        max: 999,
      },
      starRating: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        max: 5,
      },
      reviewScore: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        min: 0,
      },
      distanceFromCenter: {
        isOn: false,
        defaultValue: "",
        value: "",
        max: 999,
      },
      propertyType: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        options: [],
      },
      meals: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        options: [],
      },
      serpFilters: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        options: [],
      },
      roomAmenities: {
        isOn: false,
        defaultValue: "any",
        value: "any",
        options: [],
      },
      textFilter: {
        isOn: false,
        defaultValue: "",
        value: "",
      },
      mapBounds: {
        isOn: false,
        defaultValue: null,
        value: null,
        externallyControlledItems: [],
        max: { north: 0, south: 0, west: 0, east: 0 },
      },
    },
  },
  {
    setFilterOptions: (state, action) => {
      const { key, options } = action.payload;
      if (state.filters[key]) {
        state.filters[key].options = options;
      }
    },
    setExternallyControlledItems(state, action) {
      console.log("%csetting map included items", "color: black; background: red");
      state.filters.mapBounds.externallyControlledItems = action.payload;
    }
  }
);

export const flightFilterActions = flightsSlice.actions;
export const hotelFilterActions = hotelsSlice.actions;

export const selectFilters = (state, filterType = "flights", type) => {
  if (type) {
    return state[filterType].filters[type];
  } else {
    return state[filterType].filters;
  }
};

export const selectSort = (state, filterType = "flights") => state[filterType].sort.type;

export const selectIsAnyFilterOn =
  (filterType = "flights") =>
  (state) => {
    return Object.values(state[filterType].filters).some((f) => f.isOn);
  };

export const selectActiveFilters = createSelector(
  [(state, filterType) => selectFilters(state, filterType)],
  (filters) => {
    const activeFilterTypes = Object.entries(filters)
      .filter((entry) => entry[1].isOn)
      .map((entry) => entry[0]);
    const activeFilters = {};
    activeFilterTypes.forEach((ftype) => {
      activeFilters[ftype] = filters[ftype].value;
    });
    return activeFilters;
  }
);

const selectDepartureStart = (state, filterType) =>
  state[filterType].filters.departureStartTime;
const selectDepartureEnd = (state, filterType) => state[filterType].filters.departureEndTime;
const selectReturnStart = (state, filterType) => state[filterType].filters.returnStartTime;
const selectReturnEnd = (state, filterType) => state[filterType].filters.returnEndTime;

export const selectTimesFilters = createSelector(
  [
    (state, filterType) => selectDepartureStart(state, filterType),
    (state, filterType) => selectDepartureEnd(state, filterType),
    (state, filterType) => selectReturnStart(state, filterType),
    (state, filterType) => selectReturnEnd(state, filterType),
  ],
  (start1, end1, start2, end2) => {
    return {
      depStart: start1,
      depEnd: end1,
      retStart: start2,
      retEnd: end2,
    };
  }
);
